// Parse the `level` attribute on heading-bearing custom tags
// (`<collapsible-heading>`, etc.) into the two props Chakra's `Heading`
// accepts: a semantic element via `as` (h2..h6) and a typographic size.
//
// Inputs accepted: numeric strings ("2"), numeric values (2), and the
// HTML-idiomatic "h2"/"H3" form. Levels are clamped to h2..h6; h1 is
// reserved for the page-level title outside the markdown body. Anything
// else returns `{ as: undefined, size: undefined }` so the caller can
// fall back to the component's defaults.

const SIZE_BY_LEVEL = {
  2: "xl",
  3: "lg",
  4: "md",
  5: "md",
  6: "md",
} as const;

export type HeadingAs = "h2" | "h3" | "h4" | "h5" | "h6";
export type HeadingSize = (typeof SIZE_BY_LEVEL)[keyof typeof SIZE_BY_LEVEL];

export interface ParsedHeadingLevel {
  as: HeadingAs | undefined;
  size: HeadingSize | undefined;
}

export function parseHeadingLevel(level: unknown): ParsedHeadingLevel {
  if (typeof level !== "string" && typeof level !== "number") {
    // null, undefined, boolean attrs (e.g. `open` parsed as bare attribute)
    // shouldn't be coerced to a numeric level.
    return { as: undefined, size: undefined };
  }
  const stripped = typeof level === "string" ? level.replace(/^h/i, "") : level;
  const parsed =
    typeof stripped === "string" ? parseInt(stripped, 10) : stripped;
  if (!Number.isFinite(parsed)) {
    return { as: undefined, size: undefined };
  }
  // Floor after the clamp so a fractional level (e.g. `level={2.5}`
  // passed directly as a JS number) collapses to a valid integer rung
  // rather than producing `as="h2.5"`.
  const clamped = Math.floor(
    Math.min(6, Math.max(2, parsed)),
  ) as keyof typeof SIZE_BY_LEVEL;
  return {
    as: `h${clamped}` as HeadingAs,
    size: SIZE_BY_LEVEL[clamped],
  };
}
